import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Nosto-sourced product carousel (see sections/ts-nosto-horiz-carousel.liquid).
 *
 * The Nosto app embed injects a data-only payload into the placement div
 * (`data-title` on the wrapper; `data-url` + `data-product-id` per product).
 * This component reads it, fetches each card server-rendered via the Section
 * Rendering API, and mounts the carousel shell from the inert <template>.
 * Click attribution rides on the Nosto URL params; add-to-carts are reported
 * via `api.recommendedProductAddedToCart`.
 *
 * Two disruptions are absorbed with caches:
 * - Cart morphs (adds/quantity changes re-render the header section) sync the
 *   drawer back to server HTML without reconnecting this element — the shell
 *   is wiped and the template restored. Fetched cards are cached per handle,
 *   so the rebuild remounts instantly instead of refetching.
 * - Page navigations restart the Nosto boot chain from zero. The drawer
 *   instance persists its payload in sessionStorage, so card fetches start at
 *   page load instead of after Nosto's script + recommendations round trip.
 *
 * Nosto stays authoritative over both caches: a payload that differs from the
 * mounted one (products, attribution params, or title) remounts over it, and
 * an empty first render purges the caches and unmounts.
 *
 * @typedef {object} Refs
 * @property {HTMLTemplateElement} [carouselTemplate]
 * @property {HTMLElement} [placement]
 *
 * @extends {Component<Refs>}
 */
class NostoCarousel extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Signature of the payload currently mounted, '' when nothing is. */
  #mountedSignature = '';

  /** Signature of the in-flight build, '' when idle. */
  #buildSignature = '';

  /**
   * Pristine shell markup, captured from the template on first mount —
   * mounting consumes the template, and a payload change after mount must be
   * able to rebuild without it.
   *
   * @type {DocumentFragment | null}
   */
  #shellBlueprint = null;

  /** @type {Set<string>} */
  #campaignProductIds = new Set();

  /** @type {Set<string>} */
  #reportedProductIds = new Set();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #emptyTimer = null;

  #persistPayloads = false;

  #sawPostrender = false;

  /**
   * The placement div, queried live — refs can lag behind a cart morph that
   * replaces the node (the framework's refs observer registers on
   * requestIdleCallback) and point at the detached pre-morph placement.
   */
  get #placement() {
    return this.querySelector('[ref="placement"]');
  }

  connectedCallback() {
    super.connectedCallback();

    getNostojs()((api) =>
      api.listen('postrender', () => {
        // Nosto has finished a render pass: its payload is authoritative.
        if (this.#placement?.querySelector('[data-url]')) {
          this.#tryBuild();
        } else if (!this.#sawPostrender) {
          // The first pass covers every placement present at boot, so an empty
          // placement means Nosto answered with no recommendations — any
          // seeded or mounted payload is stale. Later passes may not include
          // this placement (and a cart morph empties it), so emptiness is
          // only conclusive on the first one.
          this.#handleNostoEmpty();
        } else {
          // Rebuild from cache if a morph wiped the shell; collapse the
          // (drawer) loading section if nothing ever arrived.
          this.#tryBuild();
          this.#collapseIfEmpty();
        }
        this.#sawPostrender = true;
      })
    );

    // One observer covers both build triggers: Nosto injecting a payload into
    // the placement, and a cart morph wiping the mounted shell. The morph can
    // replace the placement node itself, so observe the whole subtree.
    this.#observer = new MutationObserver(() => this.#tryBuild());
    this.#observer.observe(this, { childList: true, subtree: true });

    // Document-level: quick-add modal adds originate outside this subtree.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Cross-navigation persistence is drawer-only: the cart placement's
    // recommendations are cart-scoped, while PDP placements vary per product
    // and must wait for Nosto.
    this.#persistPayloads = Boolean(this.querySelector('.ts-nosto-cart-carousel__loading'));

    // Payload may already be present (Nosto rendered first, or editor
    // re-render), or persisted from a previous page.
    this.#tryBuild();

    // Fallback for when Nosto's JS never loads at all (e.g. blocked) and no
    // `postrender` ever fires — otherwise the drawer would show the skeleton
    // forever. Scoped to instances with the loading placeholder (the drawer);
    // `postrender` handles the normal empty response well before this fires.
    if (this.querySelector('.ts-nosto-cart-carousel__loading')) {
      this.#emptyTimer = setTimeout(() => this.#collapseIfEmpty(), 8000);
    }
  }

  /**
   * Collapses the section when the placement has no products and nothing has
   * mounted. Only acts on instances that opt in with a loading placeholder.
   */
  #collapseIfEmpty() {
    if (!this.querySelector('.ts-nosto-cart-carousel__loading')) return;
    if (this.querySelector('.ts-nosto-cart-carousel__shell')) return;
    if (this.#placement?.querySelector('[data-url]')) return;
    this.setAttribute('data-nosto-empty', '');
  }

  /**
   * Nosto authoritatively answered with no recommendations: any seeded or
   * mounted payload is stale. Cancel in-flight work, purge the caches so no
   * rebuild or future navigation reseeds it, unmount, and collapse.
   */
  #handleNostoEmpty() {
    this.#activeFetch?.abort();
    this.#buildSignature = '';
    this.#mountedSignature = '';

    const placementId = this.#placement?.id ?? '';
    payloadCache.delete(placementId);
    if (placementId) {
      try {
        sessionStorage.removeItem(SESSION_KEY_PREFIX + placementId);
      } catch {
        // Storage unavailable — nothing persisted to purge.
      }
    }

    for (const shell of this.querySelectorAll('[data-ts-nosto-mounted]')) shell.remove();
    this.#collapseIfEmpty();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#activeFetch?.abort();
    if (this.#emptyTimer) clearTimeout(this.#emptyTimer);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Builds and mounts the carousel once a payload with products is available.
   * Idempotent: re-entrant calls for the same payload no-op, a wiped shell
   * remounts from cache, and a changed payload supersedes the in-flight build.
   */
  async #tryBuild() {
    const placementId = this.#placement?.id ?? '';

    let payload = this.#extractPayload();
    if (payload.items.length) {
      payloadCache.set(placementId, payload);
      if (this.#persistPayloads) persistPayload(placementId, payload);
    } else {
      // Editor re-renders and cart morphs empty the placement without
      // re-triggering Nosto; fresh navigations start with it empty too.
      const cached =
        payloadCache.get(placementId) ?? (this.#persistPayloads ? readPersistedPayload(placementId) : null);
      if (!cached?.items.length) return;
      payload = cached;
      payloadCache.set(placementId, payload);
    }

    // A cart morph syncs children back to server HTML on the same element
    // instance: shell gone, inert template restored. Treat as unmounted so
    // the build below remounts (from cache, so no skeleton dwell).
    if (this.#mountedSignature && this.querySelector('template[ref="carouselTemplate"]')) {
      this.#mountedSignature = '';
    }

    const signature = payloadSignature(payload);
    if (signature === this.#mountedSignature) return;
    if (signature === this.#buildSignature) return;

    this.#buildSignature = signature;

    try {
      const { slides, stylesheet } = await this.#fetchSlides(payload.items);
      if (this.#buildSignature !== signature) return;
      if (!slides.length) return;

      if (!this.#mount(slides, stylesheet, payload.title)) return;
      this.#mountedSignature = signature;

      for (const item of payload.items) {
        if (item.productId) this.#campaignProductIds.add(item.productId);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      // Leave the signature cleared so the next postrender/mutation retries.
      console.error('Nosto carousel error:', error);
    } finally {
      if (this.#buildSignature === signature) this.#buildSignature = '';
    }
  }

  /**
   * Reads the injected payload, deduped by handle.
   *
   * @returns {NostoPayload}
   */
  #extractPayload() {
    const placement = this.#placement;
    if (!placement) return { items: [], title: '' };

    /** @type {NostoItem[]} */
    const items = [];

    for (const element of placement.querySelectorAll('[data-url]')) {
      const url = element.getAttribute('data-url') ?? '';
      const handle = extractHandle(url);
      if (!handle || items.some((item) => item.handle === handle)) continue;

      items.push({ handle, url, productId: element.getAttribute('data-product-id') ?? '' });
    }

    const title = placement.querySelector('[data-title]')?.getAttribute('data-title')?.trim() ?? '';

    return { items, title };
  }

  /**
   * Resolves the server-rendered slide per product — from the module cache
   * when available (post-morph remounts), otherwise fetched via the Section
   * Rendering API. Individual failures are skipped. Also captures the SFR
   * stylesheet from the first fetched response.
   *
   * @param {NostoItem[]} items
   * @returns {Promise<{ slides: Element[], stylesheet: Element | null }>}
   */
  async #fetchSlides(items) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const { signal } = this.#activeFetch;
    const sectionId = this.dataset.cardSectionId || 'ts-nosto-product-card';
    const parser = new DOMParser();

    /** @type {Element | null} */
    let stylesheet = null;

    const results = await Promise.all(
      items.map(async (item) => {
        const cached = slideCache.get(item.handle);
        if (cached) {
          const slide = /** @type {Element} */ (cached.cloneNode(true));
          applyAttributionParams(slide, item.url);
          return slide;
        }

        try {
          const response = await fetch(`/products/${encodeURIComponent(item.handle)}?section_id=${sectionId}`, {
            signal,
          });
          if (!response.ok) return null;

          const text = await response.text();
          const doc = parser.parseFromString(text, 'text/html');
          stylesheet ??= doc.querySelector('style[data-section-stylesheet]');

          const slide = doc.querySelector('.ts-carousel-product-card');
          if (slide) {
            // Cache pre-attribution: Nosto URL params can differ per payload.
            slideCache.set(item.handle, /** @type {Element} */ (slide.cloneNode(true)));
            applyAttributionParams(slide, item.url);
          }
          return slide;
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') throw error;
          return null;
        }
      })
    );

    this.#activeFetch = null;
    cardStylesheet ??= stylesheet;
    return { slides: results.filter((slide) => slide instanceof Element), stylesheet: stylesheet ?? cardStylesheet };
  }

  /**
   * Populates the shell and swaps it in for the template — or, when the
   * template is already consumed by an earlier mount, for the previously
   * mounted shell (a changed Nosto payload must supersede a mounted one).
   * When neither exists (an authoritative-empty pass unmounted), the shell is
   * inserted fresh before the placement.
   *
   * @param {Element[]} slides
   * @param {Element | null} stylesheet
   * @param {string} title
   * @returns {boolean} Whether the shell was mounted.
   */
  #mount(slides, stylesheet, title) {
    // Query live: refs can lag behind a cart morph (the framework's refs
    // observer registers on requestIdleCallback) and point at the detached
    // pre-morph template.
    const template = this.querySelector('template[ref="carouselTemplate"]');
    if (template instanceof HTMLTemplateElement) {
      this.#shellBlueprint = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    }
    if (!this.#shellBlueprint) return false;

    const content = /** @type {DocumentFragment} */ (this.#shellBlueprint.cloneNode(true));
    const scroller = content.querySelector('slideshow-slides');
    const shell = content.firstElementChild;
    if (!scroller || !shell) return false;

    shell.setAttribute('data-ts-nosto-mounted', '');

    for (const slide of slides) scroller.append(slide);

    const headline = content.querySelector('[data-ts-nosto-title]');
    if (headline) {
      if (title) {
        headline.textContent = title;
      } else {
        (headline.closest('.ts-horiz-carousel__headline') ?? headline).remove();
      }
    }

    // The shell renders with slide_count 0, which disables the next arrow.
    if (slides.length > 1) content.querySelector('[ref="next"]')?.removeAttribute('disabled');

    // Stylesheet tags are page-scoped; the SFR response carries the card CSS.
    // Cart morphs strip the prepended clone, so re-check on every mount.
    if (stylesheet && !document.querySelector('style[data-ts-nosto-card-styles]')) {
      const styleClone = /** @type {HTMLStyleElement} */ (stylesheet.cloneNode(true));
      styleClone.setAttribute('data-ts-nosto-card-styles', '');
      this.prepend(styleClone);
    }

    // Mount target: the inert template, the shell a previous build mounted, or
    // (when an unmount removed both) fresh insertion before the placement.
    const target = template ?? this.querySelector('[data-ts-nosto-mounted]');
    if (target) {
      target.replaceWith(content);
    } else {
      const anchor = this.#placement;
      if (!anchor) return false;
      anchor.before(content);
    }

    // Defensive: a morph can restore the template while an old mounted shell
    // survives the diff — never leave two shells in the DOM.
    for (const mounted of this.querySelectorAll('[data-ts-nosto-mounted]')) {
      if (mounted !== shell) mounted.remove();
    }

    // Mounted: cancel the empty-collapse fallback and undo any earlier collapse.
    if (this.#emptyTimer) {
      clearTimeout(this.#emptyTimer);
      this.#emptyTimer = null;
    }
    this.removeAttribute('data-nosto-empty');

    return true;
  }

  /**
   * Reports add-to-carts of campaign products to Nosto, once per product.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    if (!(event instanceof CustomEvent) && !('detail' in event)) return;

    const productId = String(/** @type {any} */ (event).detail?.data?.productId ?? '');
    if (!productId || !this.#campaignProductIds.has(productId)) return;
    if (this.#reportedProductIds.has(productId)) return;

    this.#reportedProductIds.add(productId);
    const placementId = this.#placement?.id ?? '';
    getNostojs()((api) => api.recommendedProductAddedToCart(productId, placementId));
  };
}

/**
 * @typedef {object} NostoItem
 * @property {string} handle
 * @property {string} url - Nosto product URL, including attribution params.
 * @property {string} productId
 */

/**
 * @typedef {object} NostoPayload
 * @property {NostoItem[]} items
 * @property {string} title
 */

/**
 * Payloads per placement id — editor section re-renders and cart morphs don't
 * re-trigger Nosto, so rebuilds read from here.
 *
 * @type {Map<string, NostoPayload>}
 */
const payloadCache = new Map();

/**
 * Fetched cards per product handle, stored pre-attribution — post-morph
 * remounts clone from here instead of refetching.
 *
 * @type {Map<string, Element>}
 */
const slideCache = new Map();

/**
 * The SFR card stylesheet from the first fetched response, kept for
 * cache-only rebuilds.
 *
 * @type {Element | null}
 */
let cardStylesheet = null;

const SESSION_KEY_PREFIX = 'ts-nosto-payload:';

/**
 * Payload identity. Covers URLs, product ids, and title — not just handles —
 * so a Nosto render that only changes attribution params or the campaign
 * title still supersedes a mounted payload.
 *
 * @param {NostoPayload} payload
 * @returns {string}
 */
const payloadSignature = (payload) =>
  JSON.stringify([payload.title, payload.items.map((item) => [item.url, item.productId])]);

/**
 * @param {string} placementId
 * @param {NostoPayload} payload
 */
const persistPayload = (placementId, payload) => {
  if (!placementId) return;

  try {
    sessionStorage.setItem(SESSION_KEY_PREFIX + placementId, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode, quota) — persistence is best-effort.
  }
};

/**
 * @param {string} placementId
 * @returns {NostoPayload | null}
 */
const readPersistedPayload = (placementId) => {
  if (!placementId) return null;

  try {
    const raw = sessionStorage.getItem(SESSION_KEY_PREFIX + placementId);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return null;

    /** @type {NostoItem[]} */
    const items = [];
    for (const item of parsed.items) {
      if (typeof item?.handle !== 'string' || typeof item?.url !== 'string') continue;
      items.push({
        handle: item.handle,
        url: item.url,
        productId: typeof item.productId === 'string' ? item.productId : '',
      });
    }
    if (!items.length) return null;

    return { items, title: typeof parsed.title === 'string' ? parsed.title : '' };
  } catch {
    return null;
  }
};

/**
 * Merges the Nosto URL's attribution params into the slide's product links
 * and `data-product-url` attributes, preserving existing params.
 *
 * @param {Element} slide
 * @param {string} nostoUrl
 */
const applyAttributionParams = (slide, nostoUrl) => {
  /** @type {URLSearchParams} */
  let params;
  try {
    params = new URL(nostoUrl, window.location.origin).searchParams;
  } catch {
    return;
  }
  if ([...params.keys()].length === 0) return;

  /**
   * @param {string} value
   * @returns {string}
   */
  const withParams = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      for (const [key, param] of params) {
        if (!url.searchParams.has(key)) url.searchParams.set(key, param);
      }
      return url.pathname + url.search + url.hash;
    } catch {
      return value;
    }
  };

  for (const anchor of slide.querySelectorAll('a[href*="/products/"]')) {
    const href = anchor.getAttribute('href');
    if (href) anchor.setAttribute('href', withParams(href));
  }

  for (const element of slide.querySelectorAll('[data-product-url]')) {
    const value = element.getAttribute('data-product-url');
    if (value) element.setAttribute('data-product-url', withParams(value));
  }
};

/**
 * Returns the global `nostojs` command queue, installing the standard stub
 * if the Nosto include script hasn't loaded yet.
 *
 * @returns {NostoJS}
 */
const getNostojs = () => {
  if (!window.nostojs) {
    /** @type {NostoJS} */
    const stub = (callback) => {
      (stub.q = stub.q || []).push(callback);
    };
    window.nostojs = stub;
  }

  return window.nostojs;
};

/**
 * @param {string | null} url
 * @returns {string | null}
 */
const extractHandle = (url) => {
  if (!url) return null;

  try {
    const { pathname } = new URL(url, window.location.origin);
    const match = pathname.match(/\/products\/([^/?#]+)/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
};

if (!customElements.get('ts-nosto-carousel')) {
  customElements.define('ts-nosto-carousel', NostoCarousel);
}
